import { endIntercomSession } from '@/services/intercomReboot';
import { useGetOrganizationDetailsQuery } from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import Cookies from 'js-cookie';
import { LDContext, LDSingleKindContext, useLDClient } from 'launchdarkly-react-client-sdk';
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export interface IAuthContext {
  login: (state?: string, impersonateOrg?: string) => Promise<void>;
  logout: () => Promise<void>;
  register?: (state?: string) => Promise<void>;
  user?: Record<string, any>;
  isAuthenticated: boolean;
  refreshToken: () => Promise<void>;
  claims?: Record<string, any>;
  organizationData?: OrganizationNode;
  homeOrganizationData?: OrganizationNode;
  authenticatedLDContext?: LDSingleKindContext;
}

export type RedirectSuccess = (state: string) => void;
export type RedirectFail = (error: any) => void;

// Lower bound so a short-lived/just-expiring token can't busy-loop the refresh effect
// (negative timeDiff would otherwise yield setTimeout(..., 0) → immediate re-fire).
const MIN_REFRESH_DELAY_MS = 30_000;

export interface AuthConfig extends PropsWithChildren {
  clientId?: string;
  scope?: string;
}

export const AuthContext = React.createContext<IAuthContext>({
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  user: {},
  organizationData: {} as OrganizationNode,
  isAuthenticated: false,
  refreshToken: () => Promise.resolve(),
});

// Module-level constants for the home-org-fetch backoff retry schedules.
// Hoisted out of the component body so:
//   - they're not re-created on every render (these are read inside a
//     useEffect; defining them in the body forces a fresh array each
//     render and creates a non-stable reference)
//   - react-hooks/exhaustive-deps doesn't flag them as missing deps
//     when referenced inside the useEffect below
//   - if they're ever made configurable (e.g. from a hook), moving
//     them out forces a conscious decision rather than hiding a
//     stale-closure bug behind "they're constants anyway"
// Two schedules keyed by error class:
//   - non-throttle (5xx/network): 5s/15s/30s — fast feedback for
//     transient issues that typically self-heal within a request cycle.
//   - 429 throttle: 30s/60s/120s — respects backoff so retries don't
//     prolong the rate-limit window.
const HOME_ORG_RETRY_DELAYS_MS = [5_000, 15_000, 30_000];
const HOME_ORG_RETRY_DELAYS_MS_THROTTLE = [30_000, 60_000, 120_000];

export const AuthProvider: React.FC<AuthConfig> = (props) => {
  const { children, clientId, scope } = props;
  const ldClient = useLDClient();

  const user = useMemo(() => {
    const userCookie = Cookies.get('user');
    if (userCookie) {
      try {
        return JSON.parse(userCookie);
      } catch {
        // Log only that parsing failed — NOT the cookie value. The user
        // cookie contains Auth0 profile data (name, email, etc.) which
        // is PII and must not land in the browser console where
        // screenshares, extensions, or shoulder-surfers can capture it.
        // The cookie length is OK to log as a diagnostic signal.
        console.error(`Failed to parse user cookie (length=${userCookie.length})`);
      }
    }
  }, []);

  const claims = useMemo(() => {
    const claimsCookie = Cookies.get('claims');
    if (claimsCookie) {
      try {
        return JSON.parse(claimsCookie);
      } catch {
        // Log only that parsing failed — NOT the claims value. Claims
        // include the user's email, org alias, root org, and permission
        // set — JWT-derived PII that must not surface in the console.
        // The cookie length is OK to log as a diagnostic signal.
        console.error(`Failed to parse claims cookie (length=${claimsCookie.length})`);
      }
    }
  }, []);

  const { data: organizationData, error } = useGetOrganizationDetailsQuery(
    { alias: claims?.org },
    { skip: !claims?.org },
  );

  // The user's home (root) org — distinct from the current view, which changes during impersonation.
  // Intercom Messenger boot must key off this so impersonation never reassigns the user across companies.
  // When not impersonating (org === rootOrg, OR rootOrg is absent because
  // the user is a non-admin who cannot impersonate) we reuse
  // organizationData; we only fire a second query when the user is
  // actively viewing a different org than their home one.
  //
  // Note on rootOrg semantics: the Sabal invariant is that `claims.rootOrg`
  // is only emitted during an active impersonation session. A non-admin
  // user has `claims.org` set and `claims.rootOrg` absent — that's not a
  // bug, that's "user logged in as themselves." Treating absent rootOrg
  // as a fail-closed condition would suppress Intercom for every
  // non-impersonating user, which is wrong. Security review surfaced a
  // theoretical concern ("Auth0 Rule outage drops rootOrg mid-
  // impersonation") but that case is indistinguishable from the legit
  // non-admin case using frontend signals only. The audit ledger and LD
  // gates are the real defense; the frontend correctly mirrors the JWT.
  const isImpersonating = !!claims?.org && !!claims?.rootOrg && claims.org !== claims.rootOrg;
  const {
    data: fetchedHomeOrg,
    isError: homeOrgIsError,
    error: homeOrgError,
    refetch: refetchHomeOrg,
  } = useGetOrganizationDetailsQuery({ alias: claims?.rootOrg }, { skip: !claims?.rootOrg || !isImpersonating });

  // Backoff retry for non-429 home-org fetch failures. Without this, an
  // impersonating admin whose home-org request hits a transient 5xx or
  // network blip would be stuck with `homeOrganizationData === undefined`
  // for the entire session — the `AuthenticatingWrapper` guard silently
  // suppresses Intercom and the only signal is a DevTools console.warn.
  // The retry recovers without a page reload while remaining bounded so
  // a sustained outage doesn't busy-loop the network.
  const homeOrgRetryCountRef = useRef(0);
  const homeOrgRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Retry-delay schedules live at module level (see HOME_ORG_RETRY_DELAYS_MS
  // and HOME_ORG_RETRY_DELAYS_MS_THROTTLE above). The counter is shared
  // across error classes so total retry budget stays bounded — an
  // error-class-flapping API can't drain 6 retries by alternating 5xx
  // and 429.

  // Reset the retry counter when the home org actually loads — a
  // subsequent transient failure later in the session gets a fresh
  // bounded retry budget rather than inheriting an exhausted one.
  useEffect(() => {
    if (fetchedHomeOrg) {
      homeOrgRetryCountRef.current = 0;
    }
  }, [fetchedHomeOrg]);

  // Surface a non-429 home-org fetch failure to the console AND schedule
  // a backoff retry. We intentionally do NOT fall back to organizationData
  // (the impersonated view) — that would reintroduce the very bug this PR
  // fixes (Intercom identity follows impersonation). Operators
  // investigating "Intercom isn't loading" for an admin during
  // impersonation should grep for this log line.
  useEffect(() => {
    if (!isImpersonating) {
      // Stopping impersonation drops the home-org query (skip:true) and
      // clears homeOrgIsError. The retry budget must reset too — without
      // this, an exhausted prior session (counter at MAX) leaks into the
      // next impersonation session and the very first failure logs
      // "retries exhausted" with zero retries actually attempted. The
      // success-path reset on `fetchedHomeOrg` doesn't cover this case
      // because an exhausted run by definition never reached success.
      homeOrgRetryCountRef.current = 0;
      return;
    }
    if (!homeOrgIsError) return;
    const status = (homeOrgError as FetchBaseQueryError)?.status;
    const isThrottle = status === 429;
    const delays = isThrottle ? HOME_ORG_RETRY_DELAYS_MS_THROTTLE : HOME_ORG_RETRY_DELAYS_MS;

    const attempt = homeOrgRetryCountRef.current;
    if (attempt >= delays.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `[authProvider] Home-org fetch failed during impersonation (rootOrg=${claims?.rootOrg}, status=${status ?? 'unknown'}) and exhausted ${delays.length} backoff retries. Intercom unavailable for this session; reload to retry.`,
      );
      return;
    }

    const delay = delays[attempt];
    // eslint-disable-next-line no-console
    console.warn(
      `[authProvider] Home-org fetch failed during impersonation (rootOrg=${claims?.rootOrg}, status=${status ?? 'unknown'}${isThrottle ? ' = 429 throttle' : ''}). Retrying in ${delay}ms (attempt ${attempt + 1}/${delays.length}).`,
    );

    if (homeOrgRetryTimerRef.current) clearTimeout(homeOrgRetryTimerRef.current);
    homeOrgRetryCountRef.current = attempt + 1;
    homeOrgRetryTimerRef.current = setTimeout(() => {
      homeOrgRetryTimerRef.current = null;
      refetchHomeOrg();
    }, delay);

    return () => {
      if (homeOrgRetryTimerRef.current) {
        clearTimeout(homeOrgRetryTimerRef.current);
        homeOrgRetryTimerRef.current = null;
      }
    };
  }, [isImpersonating, homeOrgIsError, homeOrgError, claims?.rootOrg, refetchHomeOrg]);

  const homeOrganizationData = isImpersonating ? fetchedHomeOrg : organizationData;

  // A transient org-details *data* fetch failure (5xx / 503 / network) must NOT de-authenticate a
  // user whose auth cookies are valid — doing so unmounts <App/> and, on recovery, remounts the
  // whole authenticated subtree, producing a refetch/remount loop. Only a real auth failure
  // (401/403) should gate auth, and that is already handled by baseQueryWithReauth.
  const orgAuthError =
    (error as FetchBaseQueryError)?.status === 401 || (error as FetchBaseQueryError)?.status === 403;
  const isAuthenticated =
    !orgAuthError && !!(Cookies.get('access_token_expires_at') && Cookies.get('user') && Cookies.get('claims'));
  const [refreshBefore, setRefreshBefore] = useState(() => {
    const expiresAt = Cookies.get('access_token_expires_at');
    if (expiresAt) {
      // refresh the token 5 minutes before it expires
      return parseInt(expiresAt, 10) - 1000 * 60 * 5;
    }
  });
  const [refreshTimeout, setRefreshTimeout] = useState<NodeJS.Timeout | null>(null);
  const [authenticatedLDContext, setAuthenticatedLDContext] = useState<LDSingleKindContext>();

  useEffect(() => {
    if (user?.sub && ldClient && claims) {
      const context: LDContext = {
        kind: 'user',
        key: user.sub,
        anonymous: false,
        name: `${user.given_name} ${user.family_name}`,
        email: user.email,
        firstName: user.given_name,
        lastName: user.family_name,
        permissions: claims?.permissions,
        organizationId: claims?.org,
        rootOrganizationId: claims?.rootOrg,
      };
      ldClient.identify(context, undefined, () => {
        setAuthenticatedLDContext(context);
      });
    }
  }, [user, ldClient, claims]);

  const login = useCallback(
    async (state = JSON.stringify({ returnTo: window.location.href }), impersonateOrg?: string) => {
      const queryParams = {
        state,
      } as Record<string, string>;
      if (clientId) {
        queryParams.clientId = clientId;
      }

      if (scope) {
        queryParams.scope = scope;
      }

      if (impersonateOrg) {
        queryParams.org = impersonateOrg;
      }

      const url = `/api/auth/login?${new URLSearchParams(queryParams)}`;
      window.location.assign(url);
    },
    [clientId, scope],
  );

  const logout = useCallback(async () => {
    // Ending the Intercom session is part of logging out, not a side effect of
    // it: a surviving `intercom-` cookie hands the next user on a shared
    // browser the previous user's conversation history, and 400s the messenger
    // with a JWT identity mismatch once the token asserts a verified user id.
    // Installer and EPC offices share machines, so this is the common case.
    //
    // Safe before the navigation because `assign` queues the load rather than
    // interrupting the current task, and `endIntercomSession` is synchronous
    // throughout — see its doc comment for what that deliberately excludes.
    try {
      endIntercomSession();
    } catch (e) {
      // Tearing down a support widget must never be able to block logging out.
      // Intercom's shutdown is third-party code; if it throws, the user would
      // click "Log out" and stay authenticated with no signal. The server-side
      // sweep on /api/auth/logout is the backstop for whatever is left behind.
      console.error('[authProvider] Intercom teardown failed during logout', e);
    }
    window.location.assign('/api/auth/logout');
  }, []);

  const refreshToken = useCallback(async () => {
    await fetch('/api/auth/jwt-refresh', { method: 'POST' });
    const expiresAt = Cookies.get('access_token_expires_at');
    if (expiresAt) {
      // refresh the token 5 minutes before it expires
      setRefreshBefore(parseInt(expiresAt, 10) - 1000 * 60 * 5);
    }
  }, [setRefreshBefore]);

  useEffect(() => {
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
    }

    if (refreshBefore) {
      const timeDiff = new Date(refreshBefore).getTime() - Date.now();
      const timer = setTimeout(refreshToken, Math.max(timeDiff, MIN_REFRESH_DELAY_MS));
      setRefreshTimeout(timer);
    }
    // This was removed, but it looks like we still need it: https://github.com/Palmetto-Sabal/sabal/commit/a1df1ed3f2137338739f0c93877d84cac6873b96#diff-23693c7044f5f97446d4d524848b9cfa3f070eafda3758d6490d4346a20b3146
    // The exhaustive-deps rule is disabled because adding refreshTimeout
    // to the dependency array causes an infinite loop. It's only used to make
    // sure that we do not get two timeouts running at the same time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshBefore, refreshToken]);

  const value = useMemo(
    () => ({
      login,
      logout,
      refreshToken,
      isAuthenticated,
      user,
      claims,
      authenticatedLDContext,
      organizationData,
      homeOrganizationData,
    }),
    [
      login,
      logout,
      refreshToken,
      isAuthenticated,
      user,
      claims,
      authenticatedLDContext,
      organizationData,
      homeOrganizationData,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
